/**
 * HUB-114 — "Test" action on the models list: POST /llm-models/:id/probe sends
 * a fixed `Hi` prompt to the provider with the row's stored key, base URL and
 * model name, and the result is shown inline — the model's real reply on
 * success, the provider's own rejection (status + message) on failure — so the
 * admin can see WHICH parameter is wrong without leaving the list.
 *
 * Calls the hub client directly with local state (the models Edit page idiom),
 * not a Refine hook: the list's column seam test renders the columns through a
 * bare `useReactTable` harness with no Refine context.
 */
import { useState } from 'react';
import { HubClientError } from '@verybigthings/trackframe-hub-client';
import type { LlmModelProbeResponse } from '@verybigthings/trackframe-shared/schemas';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { hub } from '@/lib/hub-client';

type ProbeState =
  | { phase: 'idle' }
  | { phase: 'running' }
  | { phase: 'done'; result: LlmModelProbeResponse };

/** The one-line verdict shown beside the button; `detail` goes in the tooltip. */
export function probeResultLabel(result: LlmModelProbeResponse): { label: string; detail: string } {
  if (result.ok) {
    const reply = result.kind === 'embedding' ? 'embedding returned' : `"${result.text ?? ''}"`;
    return { label: `OK · ${result.latencyMs} ms`, detail: `Reply: ${reply}` };
  }
  const status = result.statusCode !== undefined ? `HTTP ${result.statusCode}` : 'Failed';
  return { label: `${status} · ${result.latencyMs} ms`, detail: result.error ?? 'Unknown error' };
}

/** A hub-side refusal (403 / 404 / 503) rendered in the same slot as a probe failure. */
function hubErrorResult(err: unknown): LlmModelProbeResponse {
  const message = err instanceof HubClientError ? err.message : err instanceof Error ? err.message : String(err);
  return { ok: false, kind: 'chat', latencyMs: 0, error: message };
}

export function ProbeButton({ modelId }: { modelId: string }) {
  const [state, setState] = useState<ProbeState>({ phase: 'idle' });

  const run = async () => {
    setState({ phase: 'running' });
    try {
      const result = await hub.llmModelProbe({ params: { id: modelId } });
      setState({ phase: 'done', result });
    } catch (err) {
      setState({ phase: 'done', result: hubErrorResult(err) });
    }
  };

  const verdict = state.phase === 'done' ? probeResultLabel(state.result) : null;
  return (
    <span className="inline-flex items-center gap-2">
      {verdict && (
        <span
          title={verdict.detail}
          className={cn(
            'max-w-[16rem] truncate text-xs font-medium',
            state.phase === 'done' && state.result.ok ? 'text-emerald-600' : 'text-destructive',
          )}
        >
          {verdict.label}
        </span>
      )}
      <Button size="sm" variant="ghost" onClick={run} disabled={state.phase === 'running'}>
        {state.phase === 'running' ? 'Testing…' : 'Test'}
      </Button>
    </span>
  );
}
